import { Component } from '@theme/component';

/**
 * @typedef {object} PublicReview
 * @property {number} rating
 * @property {string} quote
 * @property {string} reviewerName
 * @property {string | null} product
 * @property {boolean} verified
 * @property {string} createdAt
 */

/**
 * @typedef {object} TrustShowcaseRefs
 * @property {HTMLElement} avgRatingLabel
 * @property {HTMLElement} carousel
 * @property {HTMLElement} wall
 * @property {HTMLElement[]} fadeUp
 */

const AUTO_SCROLL_PX_PER_FRAME = 0.4;
const AUTO_SCROLL_RESUME_DELAY_MS = 1200;
const CAROUSEL_COPIES = 3;

/**
 * <trust-marquee>
 *
 * Live trust section: real paid-order count + real Judge.me reviews rendered
 * as a drag/touch/snap carousel, plus a slim "wall of trust" ticker. Falls
 * back to the Liquid-rendered static content if either fetch fails.
 *
 * @extends {Component<TrustShowcaseRefs>}
 */
class TrustMarquee extends Component {
  requiredRefs = ['avgRatingLabel', 'carousel', 'wall'];

  async connectedCallback() {
    super.connectedCallback();

    this.#setupFadeUp();

    const apiBase = this.dataset.apiBase;
    if (!apiBase) return;

    const reviewsData = await this.#fetchJson(`${apiBase}/api/reviews`);

    /** @type {PublicReview[]} */
    const reviews = reviewsData?.reviews ?? [];

    if (reviewsData?.averageRating) {
      this.refs.avgRatingLabel.textContent = String(reviewsData.averageRating);
    }

    if (reviews.length > 0) {
      this.#renderCarousel(reviews);
      this.#renderWall(reviews);
    }
  }

  /** @param {string} url */
  async #fetchJson(url) {
    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  #setupFadeUp() {
    const els = this.refs.fadeUp ?? [];
    if (els.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );

    for (const el of els) {
      el.classList.add('fade-up');
      observer.observe(el);
    }
  }

  /** @param {PublicReview[]} reviews */
  #renderCarousel(reviews) {
    const cardsHtml = reviews.map((review) => this.#reviewCardHtml(review)).join('');
    this.refs.carousel.innerHTML = cardsHtml.repeat(CAROUSEL_COPIES);

    this.#setupCarouselDrag(this.refs.carousel);
    this.#setupCarouselAutoScroll(this.refs.carousel);
    this.#setupInfiniteScroll(this.refs.carousel, reviews.length);
  }

  /** @param {PublicReview} review */
  #reviewCardHtml(review) {
    const stars = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
    const verifiedBadge = review.verified
      ? '<span class="review-card__verified">✓ Verified Purchase</span>'
      : '';
    const productLine = review.product
      ? `<span class="review-card__product">${this.#escapeHtml(review.product)}</span>`
      : '';

    return `
      <article class="review-card">
        <span class="review-card__stars" aria-hidden="true">${stars}</span>
        <p class="review-card__quote paragraph">${this.#escapeHtml(review.quote)}</p>
        <div class="review-card__meta">
          <div class="review-card__meta-left">
            <span class="review-card__name">${this.#escapeHtml(review.reviewerName)}</span>
            ${verifiedBadge}
          </div>
          ${productLine}
        </div>
      </article>
    `;
  }

  /** @param {HTMLElement} carousel */
  #setupCarouselDrag(carousel) {
    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;

    /** @param {PointerEvent} event */
    const onPointerDown = (event) => {
      if (event.pointerType !== 'mouse') return; // touch scrolls natively.

      isDown = true;
      carousel.classList.add('is-dragging');
      startX = event.clientX;
      startScrollLeft = carousel.scrollLeft;
      lastX = event.clientX;
      lastT = performance.now();
      velocity = 0;
      carousel.setPointerCapture(event.pointerId);
    };

    /** @param {PointerEvent} event */
    const onPointerMove = (event) => {
      if (!isDown) return;

      carousel.scrollLeft = startScrollLeft - (event.clientX - startX);

      const now = performance.now();
      const dt = now - lastT;
      if (dt > 0) velocity = (event.clientX - lastX) / dt;
      lastX = event.clientX;
      lastT = now;
    };

    const onPointerUp = () => {
      if (!isDown) return;
      isDown = false;
      carousel.classList.remove('is-dragging');
      this.#applyMomentum(carousel, velocity);
    };

    carousel.addEventListener('pointerdown', onPointerDown);
    carousel.addEventListener('pointermove', onPointerMove);
    carousel.addEventListener('pointerup', onPointerUp);
    carousel.addEventListener('pointercancel', onPointerUp);
  }

  /**
   * @param {HTMLElement} carousel
   * @param {number} velocity px/ms at release, positive = dragged rightward.
   */
  #applyMomentum(carousel, velocity) {
    let v = velocity * -16;

    const step = () => {
      if (Math.abs(v) < 0.5) return;
      carousel.scrollLeft += v;
      v *= 0.93;
      requestAnimationFrame(step);
    };

    if (Math.abs(v) >= 0.5) requestAnimationFrame(step);
  }

  /** @param {HTMLElement} carousel */
  #setupCarouselAutoScroll(carousel) {
    let paused = false;
    /** @type {number} */
    let resumeTimeout;

    const pause = () => {
      paused = true;
      window.clearTimeout(resumeTimeout);
    };
    const scheduleResume = () => {
      resumeTimeout = window.setTimeout(() => {
        paused = false;
      }, AUTO_SCROLL_RESUME_DELAY_MS);
    };

    carousel.addEventListener('pointerdown', pause);
    carousel.addEventListener('pointerup', scheduleResume);
    carousel.addEventListener('pointercancel', scheduleResume);
    carousel.addEventListener('wheel', () => { pause(); scheduleResume(); }, { passive: true });
    carousel.addEventListener('touchstart', pause, { passive: true });
    carousel.addEventListener('touchend', scheduleResume, { passive: true });
    // A vertical page-scroll gesture that starts over the carousel can end in
    // 'touchcancel' rather than 'touchend' — without this, auto-scroll would
    // stay paused forever after the first scroll-past on mobile.
    carousel.addEventListener('touchcancel', scheduleResume, { passive: true });

    // CSS scroll-snap fights tiny programmatic scrollLeft nudges (it keeps re-snapping
    // to the nearest card), which made the auto-scroll appear frozen — so snap is only
    // enabled while the carousel isn't being auto-driven.
    let isAutoScrolling = null;

    const tick = () => {
      const shouldAutoScroll = !paused;
      if (shouldAutoScroll !== isAutoScrolling) {
        carousel.style.scrollSnapType = shouldAutoScroll ? 'none' : '';
        isAutoScrolling = shouldAutoScroll;
      }
      if (shouldAutoScroll) carousel.scrollLeft += AUTO_SCROLL_PX_PER_FRAME;
      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }

  /**
   * Keeps a 3x-duplicated track scrolling seamlessly forever by silently
   * jumping back by one set's width whenever the visible window drifts out
   * of the middle copy (imperceptible since all copies are identical).
   * @param {HTMLElement} carousel
   * @param {number} reviewCount
   */
  #setupInfiniteScroll(carousel, reviewCount) {
    requestAnimationFrame(() => {
      const secondSetFirstCard = /** @type {HTMLElement | undefined} */ (carousel.children[reviewCount]);
      if (!secondSetFirstCard) return;

      const oneSetWidth = secondSetFirstCard.offsetLeft;
      if (oneSetWidth <= 0) return;

      carousel.scrollLeft = oneSetWidth;

      carousel.addEventListener('scroll', () => {
        if (carousel.scrollLeft < oneSetWidth * 0.5) {
          carousel.scrollLeft += oneSetWidth;
        } else if (carousel.scrollLeft > oneSetWidth * 1.5) {
          carousel.scrollLeft -= oneSetWidth;
        }
      });
    });
  }

  /** @param {PublicReview[]} reviews */
  #renderWall(reviews) {
    const sorted = [...reviews].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    const itemHtml = (review) => {
      const firstName = review.reviewerName.split(' ')[0] || review.reviewerName;
      const productPart = review.product
        ? `<span class="wall-item__sep">·</span><span class="wall-item__product">${this.#escapeHtml(review.product)}</span>`
        : '';

      return `
        <div class="wall-item">
          <span class="wall-item__avatar">${this.#escapeHtml(this.#initials(review.reviewerName))}</span>
          <span class="wall-item__check" aria-hidden="true">✓</span>
          <span class="wall-item__name">${this.#escapeHtml(firstName)}</span>
          ${productPart}
          <span class="wall-item__sep">·</span>
          <span class="wall-item__time">${this.#relativeTime(review.createdAt)}</span>
        </div>
      `;
    };

    const itemsHtml = sorted.map(itemHtml).join('');
    this.refs.wall.innerHTML = itemsHtml.repeat(2);
  }

  /** @param {string} fullName */
  #initials(fullName) {
    return fullName
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  /** @param {string} isoString */
  #relativeTime(isoString) {
    const diffMs = Date.now() - new Date(isoString).getTime();
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays <= 0) return 'today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 30) return `${diffDays} days ago`;

    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths === 1) return '1 month ago';
    if (diffMonths < 12) return `${diffMonths} months ago`;

    const diffYears = Math.floor(diffMonths / 12);
    return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
  }

  /** @param {string} text */
  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

if (!customElements.get('trust-marquee')) {
  customElements.define('trust-marquee', TrustMarquee);
}
